/**
 * DeliveryDisputePage — `/delivery/disputes`.
 *
 * Live · GET /v1/service-desk/cases?formKey=COMPLAINT_*. Disputes ride on
 * the service-desk Case model — we query the four delivery-relevant complaint
 * formKeys (delay, damage, loss, wrong delivery) and roll up the open /
 * awaiting-evidence / resolved-this-week tiles client-side. Status drill-down
 * uses the standard ServiceDeskCase status badges.
 */
import type { JSX } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Scale } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { listServiceCases, type ServiceDeskCase } from '../../lib/api/service-desk';
import { PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

const DISPUTE_FORM_KEYS = [
  'COMPLAINT_DELIVERY_DELAY',
  'COMPLAINT_DAMAGED_ITEM',
  'COMPLAINT_LOST_ITEM',
  'COMPLAINT_WRONG_DELIVERY',
] as const;

const OPEN_STATUSES = new Set(['NEW', 'OPEN', 'IN_PROGRESS', 'AWAITING_CUSTOMER', 'AWAITING_AGENT']);
const AWAITING_EVIDENCE_STATUSES = new Set(['AWAITING_CUSTOMER', 'AWAITING_AGENT', 'AWAITING_INFO']);
const RESOLVED_STATUSES = new Set(['RESOLVED', 'CLOSED']);

export function DeliveryDisputePage(): JSX.Element {
  const queries = useQueries({
    queries: DISPUTE_FORM_KEYS.map((formKey) => ({
      queryKey: ['delivery', 'disputes', formKey],
      queryFn: () => listServiceCases({ formKey, pageSize: 50 }),
      retry: false,
      staleTime: 15_000,
    })),
  });

  const denied = queries.some(
    (q) => q.isError && q.error instanceof ApiError && (q.error.status === 401 || q.error.status === 403),
  );
  const allLoading = queries.every((q) => q.isPending);
  const items: ServiceDeskCase[] = queries.flatMap((q) => (q.data?.items as ServiceDeskCase[] | undefined) ?? []);

  const open = items.filter((c) => OPEN_STATUSES.has(c.status)).length;
  const awaiting = items.filter((c) => AWAITING_EVIDENCE_STATUSES.has(c.status)).length;
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const resolvedThisWeek = items.filter(
    (c) =>
      RESOLVED_STATUSES.has(c.status) &&
      typeof c.resolvedAt === 'string' &&
      Date.parse(c.resolvedAt) >= sevenDaysAgo,
  ).length;
  const total = items.length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Delivery Disputes"
        description="Recipient disputes on POD, missing items and alleged damage."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Open disputes"
          value={denied ? '—' : allLoading ? '…' : String(open)}
          hint={denied ? 'Permission required' : 'NEW + OPEN + IN_PROGRESS'}
          icon={Scale}
          tone={denied ? 'warning' : 'default'}
        />
        <KPICard
          title="Awaiting evidence"
          value={denied ? '—' : allLoading ? '…' : String(awaiting)}
          hint="AWAITING_CUSTOMER / AGENT / INFO"
          tone="warning"
        />
        <KPICard
          title="Resolved this week"
          value={denied ? '—' : allLoading ? '…' : String(resolvedThisWeek)}
          hint="status RESOLVED/CLOSED in 7d"
          tone="success"
        />
        <KPICard
          title="Total in window"
          value={denied ? '—' : allLoading ? '…' : String(total)}
          hint="Last page · 4 form keys"
        />
      </div>

      {denied ? (
        <p className="text-sm text-amber-700">
          Permission required: <code className="font-mono">ServiceDesk/readCase</code>.
        </p>
      ) : null}

      <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="dispute-table">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
            <tr>
              <th className="text-left px-4 py-2">Case #</th>
              <th className="text-left px-4 py-2">Form</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Priority</th>
              <th className="text-left px-4 py-2">Opened</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.slice(0, 50).map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-mono text-xs">{c.caseNumber}</td>
                <td className="px-4 py-2 text-xs">{c.formKey.replace(/^COMPLAINT_/, '').replace(/_/g, ' ').toLowerCase()}</td>
                <td className="px-4 py-2">
                  <StatusBadge status={c.status} />
                </td>
                <td className="px-4 py-2 text-xs">{c.priority ?? '—'}</td>
                <td className="px-4 py-2 text-xs text-muted">
                  {c.openedAt ? new Date(c.openedAt).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
            {items.length === 0 && !allLoading && !denied ? (
              <tr>
                <td colSpan={5} className="text-center text-sm text-muted py-8">
                  No delivery dispute cases in scope.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
